import { useCallback, useState } from 'react';
import useFetch, { ServiceParams } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { User } from '../models/UserModel';
import { Events } from './authService';

const useUsersService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, val: boolean) => {
    setFetching((prev) => ({ ...prev, [key]: val }));
  }, []);

  /** 🔹 Obtener lista de usuarios */
  const list = useCallback(
    (filters?: Record<string, any>, events?: Events, signal?: AbortSignal) => {
      const qs = (params?: Record<string, any>) => {
        if (!params) return '';
        return Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&');
      };
      return doGet({
        url: `${EndPoints.USER.LIST}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: (v) => setFlag('list', v),
        signal,
        ...events,
      } as ServiceParams);
    },
    [doGet, setFlag]
  );

  /** 🔹 Crear usuario */
  const store = useCallback(
    (payload: User, events?: Events) =>
      doPost({
        url: EndPoints.USER.STORE,
        body: payload,
        setFetching: (v) => setFlag('store', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  /** 🔹 Actualizar usuario */
  const update = useCallback(
    (id: number, payload: User, events?: Events) =>
      doPut({
        url: EndPoints.USER.UPDATE.replace(':id', String(id)),
        body: payload,
        setFetching: (v) => setFlag('update', v),
        ...events,
      } as ServiceParams),
    [doPut, setFlag]
  );

  /** 🔹 Eliminar usuario */
  const remove = useCallback(
    (id: number, events?: Events) =>
      doDelete({
        url: EndPoints.USER.DELETE.replace(':id', String(id)),
        setFetching: (v) => setFlag('delete', v),
        ...events,
      } as ServiceParams),
    [doDelete, setFlag]
  );

  /** 🔹 Cargar formulario de creación */
  const create = useCallback(
    (events: Events = {}) =>
      doGet({
        url: EndPoints.USER.CREATE,
        setFetching: (v) => setFlag('create', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  /** 🔹 Cargar formulario de edición */
  const edit = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.USER.EDIT.replace(':id', String(id)),
        setFetching: (v) => setFlag('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  /** 🔹 Obtener usuario actual autenticado (me) */
  const me = useCallback(
    (events?: Events) =>
      doGet({
        url: EndPoints.USER.ME,
        setFetching: (v) => setFlag('me', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  return { fetching, list, store, update, remove, create, edit, me };
};

export default useUsersService;
